import React, { useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import { useQuery } from "react-query";
import { toast } from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";
import {
  exportShopifyCampaignAnalytics,
  getShopifyCampaignAnalytics,
} from "../../services/analyticsService";
import { formatAmount } from "../../utils/formatters";

const toYMD = (date) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** Latest end date (inclusive) allowed for a given start under a 3-calendar-month cap. */
const inclusiveMaxEndForStart = (start) => {
  const t = new Date(start);
  t.setMonth(t.getMonth() + 3);
  t.setDate(t.getDate() - 1);
  t.setHours(0, 0, 0, 0);
  return t;
};

/** Earliest start date (inclusive) allowed for a given end under a 3-calendar-month cap. */
const inclusiveMinStartForEnd = (end) => {
  const t = new Date(end);
  t.setMonth(t.getMonth() - 3);
  t.setDate(t.getDate() + 1);
  t.setHours(0, 0, 0, 0);
  return t;
};

const minDate = (a, b) => new Date(Math.min(a.getTime(), b.getTime()));

const ShopifyCampaignAnalytics = () => {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [endDate, setEndDate] = useState(() => {
    const e = new Date(today);
    e.setHours(0, 0, 0, 0);
    return e;
  });
  const [isExporting, setIsExporting] = useState(false);

  const maxEndForCurrentStart = useMemo(
    () => minDate(today, inclusiveMaxEndForStart(startDate)),
    [today, startDate],
  );
  const minStartForCurrentEnd = useMemo(
    () => inclusiveMinStartForEnd(endDate),
    [endDate],
  );

  const startDateStr = toYMD(startDate);
  const endDateStr = toYMD(endDate);

  const { data, isLoading, error, refetch, isFetching } = useQuery(
    ["shopify-campaign-analytics", startDateStr, endDateStr],
    () =>
      getShopifyCampaignAnalytics({ startDate: startDateStr, endDate: endDateStr }),
    {
      onError: (err) => {
        toast.error(err.message || "Failed to load Shopify campaign analytics");
      },
      refetchOnWindowFocus: false,
    },
  );

  const campaigns = useMemo(() => {
    const list = Array.isArray(data?.campaigns) ? data.campaigns : [];
    return [...list].sort((a, b) => b.revenue - a.revenue);
  }, [data?.campaigns]);

  const channels = useMemo(() => {
    const list = Array.isArray(data?.channels) ? data.channels : [];
    return [...list].sort((a, b) => b.revenue - a.revenue);
  }, [data?.channels]);

  const handleExportCSV = async () => {
    try {
      setIsExporting(true);
      const fileData = await exportShopifyCampaignAnalytics({
        startDate: startDateStr,
        endDate: endDateStr,
      });
      const blob = fileData instanceof Blob ? fileData : new Blob([fileData], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `shopify-campaign-analytics-${startDateStr}-to-${endDateStr}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (err) {
      toast.error(err.message || "Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <DatePicker
              selected={startDate}
              onChange={(date) => {
                if (!date) return;
                const d = new Date(date);
                d.setHours(0, 0, 0, 0);
                setStartDate(d);
                const maxEnd = minDate(today, inclusiveMaxEndForStart(d));
                let nextEnd = endDate;
                if (nextEnd < d) nextEnd = d;
                if (nextEnd > maxEnd) nextEnd = maxEnd;
                setEndDate(nextEnd);
              }}
              dateFormat="dd MMM yyyy"
              minDate={minStartForCurrentEnd}
              maxDate={endDate}
              className="border border-gray-300 rounded px-3 py-2 w-44"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <DatePicker
              selected={endDate}
              onChange={(date) => {
                if (!date) return;
                let d = new Date(date);
                d.setHours(0, 0, 0, 0);
                const maxEnd = minDate(today, inclusiveMaxEndForStart(startDate));
                if (d > maxEnd) d = maxEnd;
                setEndDate(d);
                if (d < startDate) {
                  setStartDate(d);
                  return;
                }
                const minStart = inclusiveMinStartForEnd(d);
                if (startDate < minStart) setStartDate(minStart);
              }}
              dateFormat="dd MMM yyyy"
              minDate={startDate}
              maxDate={maxEndForCurrentStart}
              className="border border-gray-300 rounded px-3 py-2 w-44"
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={handleExportCSV}
              disabled={isExporting}
              className="inline-flex items-center px-4 py-2 mr-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#049ED5] hover:bg-[#0380a8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#049ED5] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExporting ? "Exporting..." : "Export CSV"}
            </button>
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching || isExporting}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#049ED5] hover:bg-[#0380a8] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#049ED5] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isFetching ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Total Orders</p>
          <p className="text-2xl font-semibold text-gray-900">
            {data?.summary?.orders || 0}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Total Revenue</p>
          <p className="text-2xl font-semibold text-gray-900">
            {formatAmount(data?.summary?.revenue || 0)}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Campaigns</h3>
        {isLoading && (
          <div className="h-40 flex items-center justify-center text-gray-500">
            Loading...
          </div>
        )}
        {error && (
          <p className="text-red-600 py-4">
            {error.message || "Failed to load campaigns"}
          </p>
        )}
        {!isLoading && !error && campaigns.length === 0 && (
          <div className="h-40 flex items-center justify-center text-gray-500">
            No campaign data found.
          </div>
        )}
        {!isLoading && !error && campaigns.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Medium
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Orders
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {campaigns.map((item, idx) => (
                  <tr key={`${item.campaign}-${item.source}-${idx}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{item.campaign}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{item.source}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{item.medium}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.orders}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                      {formatAmount(item.revenue || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Channels</h3>
        {!isLoading && !error && channels.length === 0 && (
          <div className="h-32 flex items-center justify-center text-gray-500">
            No channel data found.
          </div>
        )}
        {!isLoading && !error && channels.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Channel
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Orders
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {channels.map((item, idx) => (
                  <tr key={`${item.channel}-${idx}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{item.channel}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.orders}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                      {formatAmount(item.revenue || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopifyCampaignAnalytics;
